import React, { useState } from 'react';
import { 
  X, 
  AlertTriangle, 
  ArrowRight, 
  Check, 
  Database, 
  Zap, 
  ShieldCheck, 
  Layers, 
  UserCheck 
} from 'lucide-react';

export type BatchSimulatorPlanKey = 'reforma_anual' | 'reforma_mensal' | 'passe_projeto_90d';

export interface BatchSimulatorExitIntentModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemsCount: number;
  totalCreditsGained: number;
  totalSuggestedValue?: number;
  taxRegimeLabel: string;
  onProceedToSave: (planKey: BatchSimulatorPlanKey) => void;
  onExistingCustomerLogin: () => void;
  onDiscardAndLeave: () => void;
}

interface PlanOption {
  key: BatchSimulatorPlanKey;
  name: string;
  badge?: string;
  price: string;
  period: string;
  billedAs: string;
  description: string;
  features: string[];
  isRecommended?: boolean;
}

const PLANS: PlanOption[] = [
  {
    key: 'reforma_mensal',
    name: 'Plano Mensal',
    price: 'R$ 29,90',
    period: '/ mês',
    billedAs: 'Recorrência mensal flexível',
    description: 'Acesso contínuo com cancelamento livre a qualquer momento.',
    features: [
      'Simulador em Lote ilimitado (CSV)',
      'Armazenamento em nuvem dos lotes',
      'Exportação para o Centro de Custos R-MAX',
    ],
  },
  {
    key: 'reforma_anual',
    name: 'Plano Anual',
    badge: 'Recomendado',
    isRecommended: true,
    price: 'R$ 299,00',
    period: '/ ano',
    billedAs: '~R$ 24,90 / mês no pagamento anual',
    description: 'Melhor custo-benefício para gestão contínua e transição da Reforma.',
    features: [
      'Histórico multi-cenários salvo em nuvem',
      'Conector com Catálogo de Produtos e Serviços',
      'Prioridade na atualização da regulamentação CBS/IBS',
      'Economia equivalente a 2 meses frente ao mensal',
    ],
  },
  {
    key: 'passe_projeto_90d',
    name: 'Passe de Projeto',
    badge: '90 Dias',
    price: 'R$ 79,90',
    period: 'taxa única',
    billedAs: 'Sem renovação automática',
    description: 'Acesso pontual de 3 meses para auditorias e migração de catálogos.',
    features: [
      '90 dias de vigência integral sem fidelidade',
      'Auditoria completa de entradas e saídas',
      'Geração de planilhas de recálculo para ERP',
    ],
  },
];

export default function BatchSimulatorExitIntentModal({
  isOpen,
  onClose,
  itemsCount,
  totalCreditsGained,
  totalSuggestedValue,
  taxRegimeLabel,
  onProceedToSave,
  onExistingCustomerLogin,
  onDiscardAndLeave,
}: BatchSimulatorExitIntentModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<BatchSimulatorPlanKey>('reforma_anual');

  if (!isOpen) return null;

  const formatBRL = (val: number) => {
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-200 font-sans">
      <div 
        className="relative w-full max-w-4xl bg-slate-100 rounded-[28px] shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-modal-title"
      >
        {/* Cabeçalho Técnico M3 */}
        <div className="px-6 py-5 bg-white border-b border-slate-200/80 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center border border-amber-200 shrink-0 mt-0.5">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <span className="inline-block text-[10px] font-black uppercase tracking-wider text-amber-800 bg-amber-100/70 px-2.5 py-0.5 rounded-md mb-1">
                Atenção: Dados Não Salvos na Sessão
              </span>
              <h2 id="exit-modal-title" className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                Deseja salvar sua simulação antes de sair?
              </h2>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
                Os parâmetros configurados, custos de entrada e preços recalculados nesta sessão serão descartados ao fechar esta página. Salve seu lote em nuvem para continuar sua análise a qualquer momento.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors shrink-0"
            aria-label="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Resumo da Sessão Atual */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200/80 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-bold text-slate-700 flex items-center gap-1.5">
              <Database className="w-4 h-4 text-emerald-700" />
              <span>{itemsCount} {itemsCount === 1 ? 'item analisado' : 'itens analisados'}</span>
            </span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600 font-medium">
              Regime: <strong className="text-slate-800">{taxRegimeLabel}</strong>
            </span>
            {totalCreditsGained > 0 && (
              <>
                <span className="text-slate-300">•</span>
                <span className="text-emerald-800 font-bold">
                  Créditos apurados: {formatBRL(totalCreditsGained)}
                </span>
              </>
            )}
          </div>

          <span className="text-[11px] text-slate-500 font-medium">
            Integração direta com o Catálogo R-MAX
          </span>
        </div>

        {/* Banner de Atalho para Cliente R-MAX Existente */}
        <div className="mx-6 mt-4 p-3.5 bg-emerald-50/80 border border-emerald-200 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-xs text-emerald-950">
            <UserCheck className="w-4 h-4 text-emerald-700 shrink-0" />
            <span>
              <strong>Já é cliente R-MAX Precificação?</strong> Acesse sua conta para transferir este lote diretamente para a seção Reforma Tributária do seu Painel.
            </span>
          </div>
          <button
            type="button"
            onClick={onExistingCustomerLogin}
            className="shrink-0 px-3.5 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs rounded-xl transition-colors flex items-center gap-1.5 shadow-xs"
          >
            <span>Entrar e abrir no meu Painel</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Corpo: Tabela de Planos Integrada (Grid M3) */}
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          <div className="text-xs text-slate-600 font-medium">
            Se ainda não possui conta com armazenamento ativo, selecione uma opção para salvar sua análise em nuvem e prosseguir:
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            {PLANS.map((plan) => {
              const isSelected = selectedPlan === plan.key;

              return (
                <div
                  key={plan.key}
                  onClick={() => setSelectedPlan(plan.key)}
                  className={`relative p-5 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between ${
                    isSelected
                      ? 'bg-white border-emerald-600 shadow-md ring-2 ring-emerald-500/20'
                      : 'bg-white/80 border-slate-200 hover:border-slate-300 hover:bg-white'
                  }`}
                >
                  {plan.badge && (
                    <span className={`absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                      plan.isRecommended
                        ? 'bg-emerald-700 text-white shadow-xs'
                        : 'bg-slate-800 text-white'
                    }`}>
                      {plan.badge}
                    </span>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-black text-slate-900 tracking-tight">
                        {plan.name}
                      </h3>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-emerald-700 bg-emerald-700' : 'border-slate-300'
                      }`}>
                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                    </div>

                    <div className="mb-2">
                      <span className="text-2xl font-black text-slate-900">
                        {plan.price}
                      </span>
                      <span className="text-xs text-slate-500 font-medium ml-1">
                        {plan.period}
                      </span>
                      <span className="block text-[10px] text-slate-400 mt-0.5">
                        {plan.billedAs}
                      </span>
                    </div>

                    <p className="text-xs text-slate-600 mb-4 leading-relaxed">
                      {plan.description}
                    </p>

                    <div className="space-y-1.5 pt-3 border-t border-slate-100">
                      {plan.features.map((feat, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-700">
                          <Check className="w-3.5 h-3.5 text-emerald-700 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 pt-3 border-t border-slate-100/80">
                    <span className={`text-[11px] font-bold block text-center ${
                      isSelected ? 'text-emerald-800' : 'text-slate-400'
                    }`}>
                      {isSelected ? '✓ Plano Selecionado' : 'Clique para selecionar'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rodapé de Ações M3 (Filled Button + Text Button) */}
        <div className="px-6 py-4 bg-white border-t border-slate-200/80 flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
          <button
            type="button"
            onClick={onDiscardAndLeave}
            className="w-full sm:w-auto px-4 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors text-center"
          >
            Descartar dados e sair
          </button>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors text-center"
            >
              Continuar editando
            </button>

            <button
              type="button"
              onClick={() => onProceedToSave(selectedPlan)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-md shadow-emerald-700/20 transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
            >
              <span>Salvar simulação e continuar</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
